import { useEffect, useState } from "react";
import api from "../lib/api";
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const ymLabel = (ym) => { const [y, m] = ym.split("-"); return `${MONTHS[+m - 1]} ${y.slice(2)}`; };
const RET = "#2563eb";
const DIS = "#f97316";
const n = (v) => Number(v || 0).toLocaleString("es-DO");

const Reasons = ({ list, color }) => (list?.length ? (
  <ul className="mt-0.5 mb-1 pl-2 space-y-0.5">
    {list.map((x) => (
      <li key={x.reason} className="flex justify-between gap-4 font-medium text-gray-300">
        <span>{x.reason}</span><span style={{ color }}>{n(x.n)} · {x.pct}%</span>
      </li>
    ))}
  </ul>
) : null);

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-semibold shadow-xl min-w-[260px]" data-testid="lp2-rvd-tooltip">
      <div className="font-extrabold mb-1">{p.label}</div>
      <div style={{ color: "#93c5fd" }}>Retail: {p.retail_rate ?? "—"}% descal. · {n(p.retail_descal)} de {n(p.retail_leads)}</div>
      <Reasons list={p.retail_reasons} color="#93c5fd"/>
      <div style={{ color: "#fdba74" }}>Distribuidor: {p.distribuidor_rate ?? "—"}% descal. · {n(p.distribuidor_descal)} de {n(p.distribuidor_leads)}</div>
      <Reasons list={p.distribuidor_reasons} color="#fdba74"/>
      <div className="mt-1 text-[10px] text-gray-400">Toca para filtrar el dashboard a este mes</div>
    </div>
  );
};

const TotalPill = ({ label, t, color, testId }) => (
  <div className="px-3 py-2 rounded-lg border" data-testid={testId}>
    <div className="text-[11px] font-extrabold uppercase tracking-wider" style={{ color }}>{label}</div>
    <div className="text-xl font-extrabold font-display">{t?.rate ?? "—"}%</div>
    <div className="text-[11px] font-bold text-gray-500">{n(t?.descal)} descalificados de {n(t?.leads)}</div>
  </div>
);

export default function RetailVsDistribuidorChart({ params, onMonthClick }) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  useEffect(() => {
    setData(null);
    api.get("/leads-digital/retail-vs-distribuidor", { params: JSON.parse(key) })
      .then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: {} }));
  }, [key]);

  const rows = (data?.rows || []).map((r) => ({ ...r, label: ymLabel(r.year_month) }));
  return (
    <section className="surface p-4 md:p-6 mb-5" data-testid="lp2-retail-vs-distribuidor">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-extrabold font-display">Calidad de Leads · Retail vs Distribuidor</h2>
          <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
            Tasa de descalificación mensual (leads CRM). Retail = campañas con SDM / Retail en el nombre ·
            Distribuidor = campañas de marca (Suzuki, Nissan, Chevrolet, Cadillac, Infiniti, Yamaha). Operación regular no se incluye. Responde a los filtros activos.
          </p>
        </div>
        {data?.totals && (
          <div className="flex gap-2">
            <TotalPill label="Retail" t={data.totals.retail} color={RET} testId="lp2-rvd-total-retail"/>
            <TotalPill label="Distribuidor" t={data.totals.distribuidor} color={DIS} testId="lp2-rvd-total-distribuidor"/>
          </div>
        )}
      </div>
      {!data ? (
        <div className="h-64 rounded-lg bg-gray-50 animate-pulse"/>
      ) : !rows.length ? (
        <div className="text-sm text-gray-500 py-10 text-center" data-testid="lp2-rvd-empty">Sin leads de campañas Retail o Distribuidor.</div>
      ) : (
        <div style={{ height: 320 }} data-testid="lp2-rvd-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 20, right: 12, left: -8, bottom: 0 }} style={{ cursor: "pointer" }}
              onClick={(e) => {
                const row = rows[Number(e?.activeIndex)] || rows.find((r) => r.label === e?.activeLabel);
                if (row && onMonthClick) onMonthClick(row.year_month);
              }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false}/>
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fontWeight: 700, fill: "#6b7280" }}/>
              <YAxis yAxisId="r" tickLine={false} axisLine={false} unit="%" domain={[0, 100]} tick={{ fontSize: 11, fill: "#9ca3af" }}/>
              <YAxis yAxisId="v" orientation="right" hide/>
              <Tooltip content={<Tip/>}/>
              <Legend verticalAlign="top" iconType="plainline" wrapperStyle={{ fontSize: 11, fontWeight: 700, paddingBottom: 12 }}/>
              <Bar yAxisId="v" name="Leads Retail" dataKey="retail_leads" fill={RET} fillOpacity={0.12} radius={[3, 3, 0, 0]}/>
              <Bar yAxisId="v" name="Leads Distribuidor" dataKey="distribuidor_leads" fill={DIS} fillOpacity={0.14} radius={[3, 3, 0, 0]}/>
              <Line yAxisId="r" name="% Descal. Retail" dataKey="retail_rate" stroke={RET} strokeWidth={2.5} dot={{ r: 3 }} connectNulls>
                <LabelList dataKey="retail_rate" position="top" formatter={(v) => (v != null ? `${v}%` : "")} style={{ fontSize: 10, fontWeight: 800, fill: RET }}/>
              </Line>
              <Line yAxisId="r" name="% Descal. Distribuidor" dataKey="distribuidor_rate" stroke={DIS} strokeWidth={2.5} dot={{ r: 3 }} connectNulls>
                <LabelList dataKey="distribuidor_rate" position="bottom" formatter={(v) => (v != null ? `${v}%` : "")} style={{ fontSize: 10, fontWeight: 800, fill: DIS }}/>
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
